import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { MailWarning, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { errorMessage } from "@/lib/errors";
import { useAuth } from "@/contexts/AuthContext";
import { useKeyboardShellMounted } from "@/hooks/useVisualViewport";

/**
 * Dismissed for the session, shared by both placements below. Module state
 * rather than a component's: closing the inline banner in a conversation must
 * not bring the floating one back on the next page, and a page reload is
 * still what ends the session, exactly as before.
 */
let dismissedForSession = false;
const dismissListeners = new Set();
const subscribeDismissed = (listener) => {
  dismissListeners.add(listener);
  return () => dismissListeners.delete(listener);
};
const dismissForSession = () => {
  dismissedForSession = true;
  dismissListeners.forEach((listener) => listener());
};

/**
 * Standing prompt for an account that has not confirmed its address.
 *
 * Mounted once beside PushOptIn, outside the router, so it survives navigation
 * and no page has to remember to render it. Dismissable for the session only —
 * kept out of localStorage on purpose, because the three actions it warns about
 * are still blocked tomorrow and a banner the user forgot dismissing is worse
 * than one that comes back.
 *
 * **Two placements** (T20-M2). `floating` is that global mount: fixed to the
 * bottom of the viewport at z-40, which is the keyboard shells' own z-index —
 * so on /dany and on a phone's conversation it painted over the composer. It
 * now steps aside while a keyboard shell is mounted, and the shell renders the
 * `inline` placement itself: a row of its column above the transcript, with
 * no position and no z-index, that the shell's height accounts for. The
 * `.shell-notice` class lets that row shrink — never the composer — when the
 * keyboard leaves too little room for both.
 */
export default function VerifyEmailBanner({ placement = "floating" }) {
  const { t } = useTranslation();
  const { user, refresh } = useAuth();
  const dismissed = React.useSyncExternalStore(
    subscribeDismissed, () => dismissedForSession, () => false);
  const shellMounted = useKeyboardShellMounted();
  const [sending, setSending] = useState(false);

  if (!user || user.email_verified !== false || dismissed) return null;
  const inline = placement === "inline";
  if (!inline && shellMounted) return null;

  const resend = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/auth/resend-verification");
      if (data?.already_verified) {
        // Verified in another tab or from the emailed link; drop the banner.
        toast.success(t("auth.verifyBanner.alreadyDone"));
        refresh();
      } else {
        toast.success(t("auth.verifyBanner.sent", { email: user.email }));
      }
    } catch (err) {
      toast.error(errorMessage(err, t("common.error")));
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className={inline
        ? "shell-notice bg-hive/95 border-b border-worker/10"
        : "fixed bottom-0 inset-x-0 z-40 bg-hive/95 backdrop-blur border-t border-worker/10"}
      data-testid="verify-email-banner"
      data-placement={placement}
      role="status"
    >
      <div className="max-w-4xl mx-auto px-4 py-3 flex items-start gap-3">
        <MailWarning size={20} className="text-worker shrink-0 mt-0.5" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-worker">
            {t("auth.verifyBanner.title")}
          </p>
          <p className="text-xs text-worker/80 mt-0.5 break-words">
            {t("auth.verifyBanner.body", { email: user.email })}
          </p>
        </div>
        <button
          type="button"
          onClick={resend}
          disabled={sending}
          className="fx-btn fx-btn--secondary text-xs whitespace-nowrap px-3 py-1.5 shrink-0"
          data-testid="verify-email-resend"
        >
          {sending
            ? <Loader2 size={14} className="animate-spin" />
            : t("auth.verifyBanner.resend")}
        </button>
        <button
          type="button"
          onClick={dismissForSession}
          className="text-worker/60 hover:text-worker shrink-0"
          aria-label={t("common.close")}
          data-testid="verify-email-dismiss"
        >
          <X size={18} />
        </button>
      </div>
    </div>
  );
}
